import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { RoleName } from '../models/role.model';
import { Permission } from '../models/permission.model';

function loginRedirect(router: Router, auth: AuthService, attemptedUrl?: string) {
  const returnUrl = auth.normalizeReturnUrl(attemptedUrl);
  if (!returnUrl) {
    return router.createUrlTree(['/login']);
  }

  auth.setReturnUrl(returnUrl);
  return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
}

/* =========================
   Basic Auth Guard
========================= */
export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.isLoggedIn) {
    return auth.isMFAVerified ? true : router.createUrlTree(['/login']);
  }

  return loginRedirect(router, auth, state.url);
};


/* =========================
   Super Admin Guard
========================= */
export const superAdminGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isLoggedIn) {
    return loginRedirect(router, auth, state.url);
  }

  if (!auth.isMFAVerified) {
    return router.createUrlTree(['/login']);
  }

  if (auth.hasRole(RoleName.SUPER_ADMIN)) {
    return true;
  }

  return router.createUrlTree(['/unauthorized']);
};


/* =========================
   Generic Role Guard (Enterprise Ready)
========================= */
export const roleGuard = (allowedRoles: RoleName[]): CanActivateFn => {
  return (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isLoggedIn) {
      return loginRedirect(router, auth, state.url);
    }

    if (!auth.isMFAVerified) {
      return router.createUrlTree(['/login']);
    }

    if (auth.hasAnyRole(allowedRoles)) {
      return true;
    }

    return router.createUrlTree(['/unauthorized']);
  };
};

/* =========================
   Permission Guard
========================= */
export const permissionGuard = (requiredPermissions: Permission[]): CanActivateFn => {
  return (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isLoggedIn) {
      return loginRedirect(router, auth, state.url);
    }

    if (!auth.isMFAVerified) {
      return router.createUrlTree(['/login']);
    }

    const hasAccess = requiredPermissions.some((permission) => auth.hasPermission(permission));
    if (hasAccess) {
      return true;
    }

    return router.createUrlTree(['/unauthorized']);
  };
};

/* =========================
   Permission Match Guard
========================= */
export const permissionMatchGuard = (requiredPermissions: Permission[]): CanMatchFn => {
  return (_route, segments) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isLoggedIn) {
      const attemptedUrl = `/${segments.map(segment => segment.path).join('/')}`;
      return loginRedirect(router, auth, attemptedUrl);
    }

    if (!auth.isMFAVerified) {
      return router.createUrlTree(['/login']);
    }

    const hasAccess = requiredPermissions.some((permission) => auth.hasPermission(permission));
    if (hasAccess) {
      return true;
    }

    return router.createUrlTree(['/unauthorized']);
  };
};
